import axios from 'axios';
import { authHeader } from './auth.header';
import config from "../environment/config";

const API_URL = config.API_URL;

// ============================================================================
// 1. JWT & TOKEN UTILITIES (RFC 6750 / Industry-Standard Inactivity Security)
// ============================================================================

/**
 * Safely parses the JSON payload from a JWT token string without external dependencies.
 * @param {string} token
 * @returns {object|null}
 */
export const parseJwt = (token) => {
    if (!token || typeof token !== 'string' || !token.includes('.')) return null;
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
};

/**
 * Checks whether a given JWT token or the current stored token is expired.
 * Includes a 5-second buffer to prevent boundary race conditions.
 * @param {string} [tokenToCheck]
 * @returns {boolean}
 */
export const isTokenExpired = (tokenToCheck) => {
    const token = tokenToCheck || localStorage.getItem('ivec_token');
    if (!token) return true;
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;
    return (payload.exp * 1000) <= Date.now();
};

/**
 * Returns the total duration of the JWT token in milliseconds.
 * Calculated from `(exp - iat) * 1000`.
 */
export const getTokenTotalDurationMs = (tokenToCheck) => {
    const token = tokenToCheck || localStorage.getItem('ivec_token');
    if (!token) return 0;
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return 0;
    if (payload.iat && payload.exp > payload.iat) {
        return Math.max(10000, (payload.exp - payload.iat) * 1000);
    }
    return 15 * 60 * 1000;
};

/**
 * Calculates remaining time in milliseconds until token expires.
 * @param {string} [tokenToCheck]
 * @returns {number}
 */
export const getTokenRemainingMs = (tokenToCheck) => {
    const token = tokenToCheck || localStorage.getItem('ivec_token');
    if (!token) return 0;
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return 0;
    const remaining = (payload.exp * 1000) - Date.now();
    return remaining > 0 ? remaining : 0;
};

/**
 * Clears all authentication-related keys from localStorage.
 */
export const clearAuthStorage = () => {
    localStorage.removeItem('ivec_token');
    localStorage.removeItem('ivec_user');
    localStorage.removeItem('ivec_role');
    localStorage.removeItem('ivec_display_name');
    localStorage.removeItem('ivec_delegate_type');
    localStorage.removeItem('user');
};

/**
 * Helper to safely resolve current active username from storage or JWT token payload.
 */
export const getActiveUsername = () => {
    // 1. Direct storage
    const direct = localStorage.getItem('ivec_user');
    if (direct && direct.trim() && direct !== 'null' && direct !== 'undefined') {
        return direct.trim();
    }
    // 2. Legacy user object storage
    try {
        const legacy = JSON.parse(localStorage.getItem('user'));
        if (legacy && (legacy.username || legacy.userName)) {
            const u = legacy.username || legacy.userName;
            if (u && typeof u === 'string' && u.trim()) return u.trim();
        }
    } catch {
        // ignore JSON parse errors
    }
    // 3. Fallback: decode subject (sub) claim from JWT token payload
    const token = localStorage.getItem('ivec_token');
    const payload = parseJwt(token);
    if (payload && payload.sub) {
        return payload.sub;
    }
    return '';
};

// ============================================================================
// 2. SESSION TIMERS, REFRESH TOKEN & EXPIRY HANDLERS
// ============================================================================

let isHandlingSessionExpiry = false;
let sessionTimer = null;
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
    refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
    refreshSubscribers.forEach((cb) => cb(token, null));
    refreshSubscribers = [];
};

const onRefreshFailed = (err) => {
    refreshSubscribers.forEach((cb) => cb(null, err));
    refreshSubscribers = [];
};

export const clearSessionTimer = () => {
    if (sessionTimer) {
        clearTimeout(sessionTimer);
        sessionTimer = null;
    }
};

/**
 * Calls backend to refresh the JWT session token.
 * Silently renews the user's session in the background so active operations
 * never get abruptly terminated.
 */
export const refreshToken = async () => {
    const currentToken = localStorage.getItem('ivec_token');
    if (!currentToken) {
        throw new Error('No session token to refresh');
    }

    try {
        const response = await axios.post(
            `${API_URL}api/auth/refreshtoken`,
            {},
            {
                headers: {
                    'Content-Type': 'application/json',
                    'isRefreshToken': 'true',
                    ...authHeader()
                }
            }
        );

        const data = response?.data?.data;
        const newToken = data?.token;
        if (newToken) {
            localStorage.setItem('ivec_token', newToken);
            if (data.role) localStorage.setItem('ivec_role', data.role);
            if (data.username) localStorage.setItem('ivec_user', data.username);
            if (data.displayName) localStorage.setItem('ivec_display_name', data.displayName);

            // Restart session timer for the fresh token
            startSessionTimer();

            // Notify all listening components (Navbar, etc.)
            if (typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('ivec:auth-changed', {
                        detail: { isAuthenticated: true, role: data.role, refreshed: true }
                    })
                );
            }
            return newToken;
        } else {
            throw new Error('No token returned in refresh response');
        }
    } catch (error) {
        console.warn('Token refresh failed:', error?.response?.status, error?.message);
        throw error;
    }
};

/**
 * Synchronizes session renewal across the application.
 */
export const startSessionTimer = () => {
    clearSessionTimer();
    const token = localStorage.getItem('ivec_token');
    if (!token) return;

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ivec:session-started'));
    }
};

/**
 * Comprehensive session expired handler:
 * - Mutex lock prevents multiple duplicate calls
 * - Clears credentials & notifies listeners
 * - Logs session expiry to backend audit trail in background
 * - Redirects cleanly to /login?sessionExpired=true
 */
export const handleSessionExpired = async () => {
    if (isHandlingSessionExpiry) return;
    isHandlingSessionExpiry = true;

    clearSessionTimer();

    // 1. Resolve username BEFORE clearing credentials from localStorage
    const username = getActiveUsername();

    // 2. Clear credentials from localStorage
    clearAuthStorage();

    // 3. Notify all listeners (Navbar, ProtectedRoute, etc.) that auth state changed
    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent('ivec:auth-changed', {
                detail: { isAuthenticated: false, reason: 'SESSION_EXPIRED' }
            })
        );
    }

    // 4. Report session expiry to backend audit trail in background
    try {
        await reportSessionExpired(username);
    } catch (e) {
        // non-blocking audit logging
    }

    isHandlingSessionExpiry = false;

    // 5. Clean redirect to login page where LoginPage will display the session expired banner
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login?sessionExpired=true';
    }
};

// ============================================================================
// 3. GLOBAL AXIOS INTERCEPTOR WITH SILENT AUTO-RETRY ON 401
// ============================================================================
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const status = error?.response?.status;
        const url = originalRequest?.url || '';

        // Exclude authentication and refresh endpoints from refresh interception
        const isExcluded =
            url.includes('/authenticate') ||
            url.includes('/api/auth/refreshtoken') ||
            url.includes('/api/auth/session-expired') ||
            url.includes('/api/auth/logout');

        if (status === 401 && !isExcluded && originalRequest) {
            const hasToken = localStorage.getItem('ivec_token');
            if (hasToken) {
                // If not already retrying, attempt transparent refresh & replay
                if (!originalRequest._retry) {
                    originalRequest._retry = true;

                    if (!isRefreshing) {
                        isRefreshing = true;
                        try {
                            const newToken = await refreshToken();
                            isRefreshing = false;
                            onRefreshed(newToken);
                            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                            return axios(originalRequest);
                        } catch (refreshErr) {
                            isRefreshing = false;
                            onRefreshFailed(refreshErr);
                            await handleSessionExpired();
                            return Promise.reject(refreshErr);
                        }
                    } else {
                        // Queue concurrent requests while token is refreshing
                        return new Promise((resolve, reject) => {
                            subscribeTokenRefresh((newToken, err) => {
                                if (err || !newToken) {
                                    reject(err || error);
                                    return;
                                }
                                originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                                resolve(axios(originalRequest));
                            });
                        });
                    }
                } else {
                    // Retry already failed -> token genuinely invalid or expired
                    await handleSessionExpired();
                }
            }
        }
        return Promise.reject(error);
    }
);

// Cross-tab logout synchronization: when user logs out in another tab, sync logout cleanly
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.key === 'ivec_token' && !e.newValue) {
            handleSessionExpired();
        }
    });
}

// ============================================================================
// 4. AUTH & SECURITY ENDPOINTS
// ============================================================================

export const authenticationDetails = async (loginData) => {
    try {
        return await axios.post(
            `${API_URL}authenticate`,
            JSON.stringify(loginData),
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            }
        );
    } catch (error) {
        console.error('Error occurred in authenticationDetails:', error);
        throw error;
    }
};

export const logoutUser = async () => {
    clearSessionTimer();
    const token = localStorage.getItem('ivec_token');

    try {
        if (token) {
            await axios.post(
                `${API_URL}api/auth/logout`,
                {},
                { headers: { 'Content-Type': 'application/json', ...authHeader() } }
            );
        }
    } catch (error) {
        console.warn('logoutUser error:', error);
    } finally {
        clearAuthStorage();
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('ivec:auth-changed', {
                    detail: { isAuthenticated: false, reason: 'LOGOUT' }
                })
            );
        }
    }
    return { success: true };
};

export const reportSessionExpired = async (username) => {
    try {
        const resolvedUser = (username && typeof username === 'string' && username.trim() && username !== 'null' && username !== 'undefined')
            ? username.trim()
            : getActiveUsername();

        const query = resolvedUser ? `?username=${encodeURIComponent(resolvedUser)}` : '';
        return (await axios.post(
            `${API_URL}api/auth/session-expired${query}`,
            {},
            {
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeader()
                }
            }
        )).data;
    } catch (error) {
        console.error('reportSessionExpired error:', error);
    }
};

export const forgotPassword = async (payload) => {
    try {
        return (await axios.post(
            `${API_URL}api/auth/forgot-password`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
        )).data;
    } catch (error) {
        console.error('forgotPassword error:', error);
        throw error;
    }
};

export const resetPassword = async (payload) => {
    try {
        return (await axios.post(
            `${API_URL}api/auth/reset-password`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
        )).data;
    } catch (error) {
        console.error('resetPassword error:', error);
        throw error;
    }
};

export const changePassword = async (payload) => {
    try {
        return (await axios.post(
            `${API_URL}api/auth/change-password`,
            payload,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('changePassword error:', error);
        throw error;
    }
};

// ============================================================================
// 5. DELEGATE REGISTRATION & VISA INFO
// ============================================================================

export const newRegister = async (data) => {
    try {
        return (await axios.post(
            `${API_URL}api/register`,
            data,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('newRegister error:', error);
        throw error;
    }
};

export const submitParticipantInfo = async (data) => {
    try {
        return (await axios.post(
            `${API_URL}api/participant-info`,
            data,
            { headers: { 'Content-Type': 'application/json' } }
        )).data;
    } catch (error) {
        console.error('submitParticipantInfo error:', error);
        throw error;
    }
};

export const getParticipantInfoList = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/participant-info`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getParticipantInfoList error:', error);
        throw error;
    }
};

// ============================================================================
// 6. PAYMENT API ENDPOINTS
// ============================================================================

export const getUserPaymentDetails = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/payment/user-details`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getUserPaymentDetails error:', error);
        throw error;
    }
};

export const createPaymentOrder = async (payload) => {
    try {
        return (await axios.post(
            `${API_URL}api/payment/create-order`,
            payload,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('createPaymentOrder error:', error);
        throw error;
    }
};

export const verifyPayment = async (payload) => {
    try {
        return (await axios.post(
            `${API_URL}api/payment/verify`,
            payload,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('verifyPayment error:', error);
        throw error;
    }
};

export const syncPaymentStatus = async (orderId) => {
    try {
        const url = orderId
            ? `${API_URL}api/payment/sync?orderId=${encodeURIComponent(orderId)}`
            : `${API_URL}api/payment/sync`;
        return (await axios.post(
            url,
            {},
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('syncPaymentStatus error:', error);
        throw error;
    }
};

// ============================================================================
// 7. ADMIN DASHBOARD & AUDIT ENDPOINTS
// ============================================================================

export const getAdminUsers = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/admin/users`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getAdminUsers error:', error);
        throw error;
    }
};

export const getAdminPaidUsers = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/admin/users/paid`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getAdminPaidUsers error:', error);
        throw error;
    }
};

export const getAdminPayments = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/admin/payments`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getAdminPayments error:', error);
        throw error;
    }
};

export const getAdminAuditStamps = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/admin/audit-stamps`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getAdminAuditStamps error:', error);
        throw error;
    }
};

export const getAdminParticipants = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/admin/participants`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getAdminParticipants error:', error);
        throw error;
    }
};

export const getAdminStats = async () => {
    try {
        return (await axios.get(
            `${API_URL}api/admin/stats`,
            { headers: { 'Content-Type': 'application/json', ...authHeader() } }
        )).data;
    } catch (error) {
        console.error('getAdminStats error:', error);
        throw error;
    }
};


export const downloadParticipantPdfDirect = async (participantId, participantName) => {
    try {
        const response = await axios.get(
            `${API_URL}api/participant-info/${participantId}/pdf`,
            {
                responseType: 'blob'
            }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeName = (participantName || participantId).replace(/[^a-zA-Z0-9_-]/g, '_');
        link.href = url;
        link.setAttribute('download', `IVEC_2027_Political_Clearance_${safeName}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
    } catch (error) {
        console.error('downloadParticipantPdfDirect error:', error);
        throw error;
    }
};

export const downloadParticipantPdfApi = async (participantId, participantName) => {
    try {
        const response = await axios.get(
            `${API_URL}api/admin/participants/${participantId}/pdf`,
            {
                headers: { ...authHeader() },
                responseType: 'blob'
            }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeName = (participantName || participantId).replace(/[^a-zA-Z0-9_-]/g, '_');
        link.href = url;
        link.setAttribute('download', `IVEC_2027_Political_Clearance_${safeName}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
    } catch (error) {
        console.error('downloadParticipantPdfApi error:', error);
        // Fallback to direct endpoint
        return downloadParticipantPdfDirect(participantId, participantName);
    }
};
